/**
 * 🚀 ELSA GESTION - Enregistrement Service Worker
 * Compatible avec React et environnement DigitalOcean
 *
 * Ce fichier permet d'enregistrer le Service Worker de manière
 * optimale pour les réseaux 3G instables (Afrique de l'Ouest)
 */

// Configuration de l'environnement
const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

// ⚡ FIX FUITE MÉMOIRE (3 Jan 2026): Variable pour cleanup de l'interval de vérification
let updateCheckInterval = null;

/**
 * Enregistrer le Service Worker
 * @param {Object} config - Configuration optionnelle avec callbacks
 */
export function register(config) {
  // Vérifier si le Service Worker est supporté et si on est en production
  if ("serviceWorker" in navigator) {
    // Attendre que la page soit complètement chargée
    window.addEventListener("load", () => {
      // URL du Service Worker (service-worker.js optimisé pour 3G)
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // Mode développement : vérifier si le SW existe
        checkValidServiceWorker(swUrl, config);

        // Log pour développeur
        navigator.serviceWorker.ready.then(() => {
          console.log(
            "[SW] 🔧 Application en mode développement. Service Worker actif.\n" +
              "En savoir plus: https://cra.link/PWA"
          );
        });
      } else {
        // Mode production : enregistrer le SW
        registerValidSW(swUrl, config);
      }
    });
  } else {
    console.warn("[SW] ⚠️ Service Worker non supporté par ce navigateur");
  }
}

/**
 * Enregistrer un Service Worker valide
 */
function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // Écouter les mises à jour du SW
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;

        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === "installed") {
            if (navigator.serviceWorker.controller) {
              // Nouveau contenu disponible (mise à jour)
              console.log(
                "[SW] 🔄 Nouvelle version disponible. Sera activée au prochain rechargement."
              );

              // Callback personnalisé si fourni
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Contenu mis en cache pour la première fois
              console.log(
                "[SW] 📦 Contenu mis en cache pour utilisation hors ligne."
              );

              // Callback personnalisé si fourni
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };

      // Vérifier les mises à jour périodiquement (toutes les heures)
      // ⚡ FIX FUITE MÉMOIRE (3 Jan 2026): Nettoyer l'ancien interval avant d'en créer un nouveau
      if (updateCheckInterval) {
        clearInterval(updateCheckInterval);
      }

      updateCheckInterval = setInterval(() => {
        if (registration && registration.update) {
          Promise.resolve(registration.update()).catch((error) => {
            console.warn("[SW] ⚠️ Échec mise à jour Service Worker:", error);
          });
        }
      }, 60 * 60 * 1000);
    })
    .catch((error) => {
      console.error("[SW] ❌ Erreur lors de l'enregistrement:", error);
    });
}

/**
 * Vérifier si le Service Worker existe (mode développement)
 */
function checkValidServiceWorker(swUrl, config) {
  // Vérifier que le service worker peut être trouvé
  fetch(swUrl, {
    headers: { "Service-Worker": "script" },
  })
    .then((response) => {
      // S'assurer que le service worker existe et qu'il s'agit d'un JS
      const contentType = response.headers.get("content-type");

      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        // Service Worker introuvable, probablement une page différente
        console.warn(
          "[SW] ⚠️ Service Worker introuvable. Désenregistrement..."
        );
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service Worker trouvé, procéder à l'enregistrement
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log("[SW] 📡 Pas de connexion internet. Mode hors ligne activé.");
    });
}

/**
 * Désenregistrer le Service Worker
 */
export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
        console.log("[SW] 🗑️ Service Worker désenregistré");
      })
      .catch((error) => {
        console.error("[SW] ❌ Erreur lors du désenregistrement:", error);
      });
  }
}

/**
 * ⚡ FIX FUITE MÉMOIRE (3 Jan 2026): Arrêter la vérification périodique des mises à jour
 * À appeler au unmount du composant principal si nécessaire
 */
export function stopUpdateCheck() {
  if (updateCheckInterval) {
    clearInterval(updateCheckInterval);
    updateCheckInterval = null;
    console.log("[SW] 🛑 Vérification périodique des mises à jour arrêtée");
  }
}

/**
 * Demander la permission pour les notifications
 * À appeler lors de l'interaction utilisateur
 */
export function requestNotificationPermission() {
  if ("Notification" in window && Notification.permission === "default") {
    return Notification.requestPermission().then((permission) => {
      console.log("[SW] 🔔 Permission notifications:", permission);
      return permission;
    });
  }
  return Promise.resolve(Notification.permission);
}

/**
 * Obtenir les informations du cache
 * Utile pour afficher la taille du cache à l'utilisateur
 */
export async function getCacheInfo() {
  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    return new Promise((resolve, reject) => {
      const messageChannel = new MessageChannel();

      messageChannel.port1.onmessage = (event) => {
        if (event.data.cacheSize !== undefined) {
          resolve({
            sizeBytes: event.data.cacheSize,
            sizeMB: (event.data.cacheSize / (1024 * 1024)).toFixed(2),
          });
        } else {
          reject(new Error("Impossible d'obtenir la taille du cache"));
        }
      };

      navigator.serviceWorker.controller.postMessage(
        { type: "GET_CACHE_SIZE" },
        [messageChannel.port2]
      );
    });
  }

  return Promise.reject(new Error("Service Worker non actif"));
}

/**
 * Nettoyer tous les caches
 * Utile pour résoudre les problèmes de cache
 */
export async function clearAllCaches() {
  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    return new Promise((resolve, reject) => {
      const messageChannel = new MessageChannel();

      messageChannel.port1.onmessage = (event) => {
        if (event.data.success) {
          console.log("[SW] 🧹 Caches nettoyés avec succès");
          resolve();
        } else {
          reject(new Error("Échec du nettoyage des caches"));
        }
      };

      navigator.serviceWorker.controller.postMessage({ type: "CLEAR_CACHE" }, [
        messageChannel.port2,
      ]);
    });
  }

  return Promise.reject(new Error("Service Worker non actif"));
}

/**
 * Forcer la mise à jour du Service Worker
 */
export function skipWaiting(registration) {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const waitingWorker = registration?.waiting;

  if (waitingWorker) {
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
    return;
  }

  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: "SKIP_WAITING" });
  }
}

// Export par défaut avec toutes les fonctions
export default {
  register,
  unregister,
  requestNotificationPermission,
  getCacheInfo,
  clearAllCaches,
  skipWaiting,
};
